import React, { useState } from 'react';
import useWindowWidth from '../../hooks/useWindowWidth';
import '../../components/MobileApp.css';
import './Dashboard.css';

// ── Desktop: dropdown multi-select ──
const MultiSelectDropdown = ({ title, items, selectedIds, onSelectAll, onClear, onSelectionChange }) => {
    return (
        <div className="dropdown">
            <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {title} ({selectedIds.length}/{items.length})
            </button>
            <ul className="dropdown-menu p-2" style={{ maxHeight: '250px', overflowY: 'auto', minWidth: '200px' }}>
                <li className="d-flex gap-1 mb-1 px-1">
                    <button className="btn btn-sm btn-outline-primary flex-fill" onClick={onSelectAll}>Todos</button>
                    <button className="btn btn-sm btn-outline-danger flex-fill" onClick={onClear}>Limpar</button>
                </li>
                <li><hr className="dropdown-divider my-1" /></li>
                {items.map(item => (
                    <li key={item.id}>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedIds.includes(item.id)}
                                onChange={() => onSelectionChange(item.id)}
                            />
                            <label className="form-check-label">{item.name}</label>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// ── Mobile: seção de checkboxes dentro do bottom sheet ──
const FilterSection = ({ title, items, selectedIds, onToggle, onSelectAll, onClear }) => (
    <div className="dash-filter-section-m">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="dash-filter-section-title-m">{title}</span>
            <div className="dash-filter-selall-row-m">
                <button className="dash-filter-selall-btn-m" onClick={onSelectAll}>Todos</button>
                <button className="dash-filter-selall-btn-m" onClick={onClear}>Limpar</button>
            </div>
        </div>
        <div className="dash-filter-check-list-m">
            {items.map(item => (
                <div
                    key={item.id}
                    className="dash-filter-check-item-m"
                    onClick={() => onToggle(item.id)}
                >
                    <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => onToggle(item.id)}
                        onClick={e => e.stopPropagation()}
                    />
                    <label>{item.name}</label>
                </div>
            ))}
        </div>
    </div>
);

const STATUS_OPTIONS = ['Paga', 'Pendente', 'Atrasada', 'Recebida'];

function DashboardFilterBar({ filters, onFiltersChange, filterOptions = { categories: [], banks: [], cards: [] } }) {
    const categories = filterOptions.categories;
    const banks = filterOptions.banks;
    const cards = filterOptions.cards;
    const [collapsed, setCollapsed] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const width = useWindowWidth();
    const isMobile = width < 768;

    const handleDateChange = (e) => {
        onFiltersChange({ ...filters, [e.target.name]: e.target.value });
    };

    const handleMultiSelectChange = (filterKey, id) => {
        const currentIds = filters[filterKey];
        const newIds = currentIds.includes(id)
            ? currentIds.filter(currentId => currentId !== id)
            : [...currentIds, id];
        onFiltersChange({ ...filters, [filterKey]: newIds });
    };

    const handleSelectAll = (filterKey, allIds) => {
        onFiltersChange({ ...filters, [filterKey]: allIds });
    };

    const handleClear = (filterKey) => {
        onFiltersChange({ ...filters, [filterKey]: [] });
    };

    const clearAll = () => {
        onFiltersChange({ ...filters, category_ids: [], bank_ids: [], card_ids: [], statuses: [] });
    };

    const bankItems = [...banks, { id: 'none', name: 'Sem banco' }];

    const activeFilterCount = filters.category_ids.length + filters.bank_ids.length +
        filters.card_ids.length + filters.statuses.length;

    // ════════════════════════════════════
    // MOBILE
    // ════════════════════════════════════
    if (isMobile) {
        return (
            <div className="dash-filter-bar-m">
                {/* Linha de datas + botão filtros */}
                <div className="dash-date-row-m">
                    <div className="dash-date-field-m">
                        <label htmlFor="m_start_date">De</label>
                        <input
                            type="date"
                            className="mapp-filter-input"
                            id="m_start_date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleDateChange}
                        />
                    </div>
                    <div className="dash-date-field-m">
                        <label htmlFor="m_end_date">Até</label>
                        <input
                            type="date"
                            className="mapp-filter-input"
                            id="m_end_date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleDateChange}
                        />
                    </div>
                    <button
                        className={`mapp-filter-btn dash-filter-btn-icon${activeFilterCount > 0 ? ' active' : ''}`}
                        onClick={() => setSheetOpen(true)}
                        style={{ flexShrink: 0, alignSelf: 'flex-end' }}
                        title="Filtros"
                    >
                        ⚙
                        {activeFilterCount > 0 && (
                            <span className="dash-filter-badge-m">{activeFilterCount}</span>
                        )}
                    </button>
                </div>

                {/* Bottom sheet */}
                {sheetOpen && (
                    <>
                        <div className="mapp-sheet-overlay" onClick={() => setSheetOpen(false)} />
                        <div className="mapp-sheet">
                            <div className="mapp-sheet-handle" />
                            <div className="mapp-sheet-header">
                                <span className="mapp-sheet-title">Filtros</span>
                                <button className="mapp-sheet-close" onClick={() => setSheetOpen(false)}>✕</button>
                            </div>
                            <div className="mapp-sheet-body">
                                <div className="dash-filter-sheet-inner">

                                    {/* Status */}
                                    <div className="dash-filter-section-m">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <span className="dash-filter-section-title-m">Status</span>
                                            <div className="dash-filter-selall-row-m">
                                                <button className="dash-filter-selall-btn-m" onClick={() => handleSelectAll('statuses', STATUS_OPTIONS)}>Todos</button>
                                                <button className="dash-filter-selall-btn-m" onClick={() => handleClear('statuses')}>Limpar</button>
                                            </div>
                                        </div>
                                        <div className="dash-filter-pills-m">
                                            {STATUS_OPTIONS.map(status => (
                                                <button
                                                    key={status}
                                                    className={`dash-filter-pill-m${filters.statuses.includes(status) ? ' active' : ''}`}
                                                    onClick={() => handleMultiSelectChange('statuses', status)}
                                                >
                                                    {status}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Categorias */}
                                    {categories.length > 0 && (
                                        <FilterSection
                                            title="Categorias"
                                            items={categories}
                                            selectedIds={filters.category_ids}
                                            onToggle={(id) => handleMultiSelectChange('category_ids', id)}
                                            onSelectAll={() => handleSelectAll('category_ids', categories.map(c => c.id))}
                                            onClear={() => handleClear('category_ids')}
                                        />
                                    )}

                                    {/* Contas */}
                                    {banks.length > 0 && (
                                        <FilterSection
                                            title="Contas Bancárias"
                                            items={bankItems}
                                            selectedIds={filters.bank_ids}
                                            onToggle={(id) => handleMultiSelectChange('bank_ids', id)}
                                            onSelectAll={() => handleSelectAll('bank_ids', bankItems.map(b => b.id))}
                                            onClear={() => handleClear('bank_ids')}
                                        />
                                    )}

                                    {/* Cartões */}
                                    {cards.length > 0 && (
                                        <FilterSection
                                            title="Cartões de Crédito"
                                            items={cards}
                                            selectedIds={filters.card_ids}
                                            onToggle={(id) => handleMultiSelectChange('card_ids', id)}
                                            onSelectAll={() => handleSelectAll('card_ids', cards.map(c => c.id))}
                                            onClear={() => handleClear('card_ids')}
                                        />
                                    )}

                                    {/* Limpar tudo */}
                                    {activeFilterCount > 0 && (
                                        <button className="dash-clear-filters-btn-m" onClick={clearAll}>
                                            Limpar todos os filtros
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ════════════════════════════════════
    // DESKTOP
    // ════════════════════════════════════
    return (
        <div className="card mb-4">
            <div
                className="card-header d-flex justify-content-between align-items-center py-2"
                style={{ cursor: 'pointer' }}
                onClick={() => setCollapsed(!collapsed)}
            >
                <span className="fw-semibold">Filtros</span>
                <span>{collapsed ? '+' : '-'}</span>
            </div>
            {!collapsed && (
                <div className="card-body">
                    <div className="d-flex flex-wrap align-items-end gap-2">
                        <div>
                            <label htmlFor="start_date" className="form-label mb-1">De</label>
                            <input
                                type="date"
                                className="form-control"
                                id="start_date"
                                name="start_date"
                                value={filters.start_date}
                                onChange={handleDateChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="end_date" className="form-label mb-1">Até</label>
                            <input
                                type="date"
                                className="form-control"
                                id="end_date"
                                name="end_date"
                                value={filters.end_date}
                                onChange={handleDateChange}
                            />
                        </div>
                        <div>
                            <MultiSelectDropdown
                                title="Categorias"
                                items={categories}
                                selectedIds={filters.category_ids}
                                onSelectAll={() => handleSelectAll('category_ids', categories.map(c => c.id))}
                                onClear={() => handleClear('category_ids')}
                                onSelectionChange={(id) => handleMultiSelectChange('category_ids', id)}
                            />
                        </div>
                        <div>
                            <MultiSelectDropdown
                                title="Contas"
                                items={bankItems}
                                selectedIds={filters.bank_ids}
                                onSelectAll={() => handleSelectAll('bank_ids', bankItems.map(b => b.id))}
                                onClear={() => handleClear('bank_ids')}
                                onSelectionChange={(id) => handleMultiSelectChange('bank_ids', id)}
                            />
                        </div>
                        <div>
                            <MultiSelectDropdown
                                title="Cartões"
                                items={cards}
                                selectedIds={filters.card_ids}
                                onSelectAll={() => handleSelectAll('card_ids', cards.map(c => c.id))}
                                onClear={() => handleClear('card_ids')}
                                onSelectionChange={(id) => handleMultiSelectChange('card_ids', id)}
                            />
                        </div>
                    </div>

                    <div className="mt-2">
                        <label className="form-label mb-1">Status</label>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {STATUS_OPTIONS.map(status => (
                                <div className="form-check" key={status}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`status-${status}`}
                                        checked={filters.statuses.includes(status)}
                                        onChange={() => handleMultiSelectChange('statuses', status)}
                                    />
                                    <label className="form-check-label" htmlFor={`status-${status}`}>{status}</label>
                                </div>
                            ))}
                            <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleSelectAll('statuses', STATUS_OPTIONS)}>Todos</button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleClear('statuses')}>Limpar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardFilterBar;
